import { type InputState, STILL, stickToMove } from "../../input/state";

/** The knob's reach in px: half the 120 px base (spec 2p §2.2). */
export const STICK_REACH = 60;
/** A thumb drag turns four times what the same pixels of mouse do, so half the screen is about 100° at sensitivity 1. */
export const TOUCH_LOOK_GAIN = 4;

export interface PointerLike { pointerId: number; clientX: number; clientY: number }

/**
 * Which finger does what (spec 2p §3.4): a press on the left half is the stick, on the right half it looks.
 * A finger keeps its job until it lifts, and each side follows one finger at a time.
 */
export class TouchRouter {
  stick: { x: number; y: number; dx: number; dy: number } | null = null;
  private stickId: number | null = null;
  private lookId: number | null = null;
  private last = { x: 0, y: 0 };

  constructor(private readonly input: InputState, private readonly width: () => number, private readonly onChange: () => void = () => {}) {}

  down(e: PointerLike): void {
    if (e.clientX < this.width() / 2) {
      if (this.stickId !== null) return;
      this.stickId = e.pointerId;
      this.stick = { x: e.clientX, y: e.clientY, dx: 0, dy: 0 };
      this.onChange();
    } else if (this.lookId === null) {
      this.lookId = e.pointerId;
      this.last = { x: e.clientX, y: e.clientY };
    }
  }

  move(e: PointerLike): void {
    if (e.pointerId === this.stickId && this.stick) {
      let dx = e.clientX - this.stick.x, dy = e.clientY - this.stick.y;
      const len = Math.hypot(dx, dy);
      if (len > STICK_REACH) { dx *= STICK_REACH / len; dy *= STICK_REACH / len; }
      this.stick = { ...this.stick, dx, dy };
      this.input.setStick(stickToMove(dx, dy, STICK_REACH));
      this.onChange();
    } else if (e.pointerId === this.lookId) {
      this.input.addLook((e.clientX - this.last.x) * TOUCH_LOOK_GAIN, (e.clientY - this.last.y) * TOUCH_LOOK_GAIN);
      this.last = { x: e.clientX, y: e.clientY };
    }
  }

  up(e: { pointerId: number }): void {
    if (e.pointerId === this.stickId) {
      this.stickId = null;
      this.stick = null;
      this.input.setStick(STILL);
      this.onChange();
    } else if (e.pointerId === this.lookId) {
      this.lookId = null;
    }
  }

  /** A cancelled pointer, a blur, a turn of the phone or a panel: forget every finger (spec 2p §4). */
  reset(): void {
    this.stickId = null;
    this.lookId = null;
    this.stick = null;
    this.input.clear();
    this.onChange();
  }
}
